/* eslint-disable quotes */
/**
 * Step One: Choose a Market
 */

import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import Link from 'next/link'
import { useAllMarkets } from '../../../services/GraphQlClientService'
import SignInText from '../../../components/sign-up-sign-in-form/SignInText'
import AddressAutocomplete from './components/AddressAutocomplete'
import LoadingStep from './components/LoadingStep'
import AddressView, { ADDRESS_VIEW_MODE } from './components/AddressView'
import { Text, Header3 } from '../../../styles/Components'
import ArrowButton from '../../ArrowButton'
import {
  AddressSection,
  MarketsSection,
  MarketButton,
  PrivacyInfo
} from './styles/one'
import { Header2 } from '../../../styles/Components'

const One = ({
  state: {
    address,
    allowAddressSelect,
    addresses,
    isAuthenticated,
    market,
    isDesktop
  },
  dispatch,
  setIsStepValid,
  me,
  next,
  nextButtonLabel,
  nextButtonEnabled,
  step
}) => {
  const ref = React.createRef()

  // valid if we have a market selected
  setIsStepValid(!!market, 'Select Your Service')

  // select the market and deselect any market services already selected
  const selectMarket = m => () => {
    if (m.id === market?.id) {
      return null
    }

    if (!allowAddressSelect) {
      ref.current.resetAddress()
    }

    return dispatch({
      allowAddressSelect: false,
      market: m,
      step: 2,
      address: {
        id: '',
        street: '',
        city: '',
        state: '',
        zip: '',
        apartment: '',
        notes: '',
        type: '',
        pets: '',
        latitude: 0,
        longitude: 0
      }
    })
  }

  // load markets
  const markets = useAllMarkets()

  // Check if returning user and has saved address
  const hasSavedAddress = isAuthenticated && me?.addresses.filter(a => !!a.marketId).length

  useEffect(() => {
    if (!markets) {
      return
    }

    if (allowAddressSelect) {
      if (!addresses) {
        const enabledMarketIds = markets.filter(m => m.isEnabled).map(m => m.id)
        const addresses = me.addresses.filter(a =>
          !!a.marketId &&
          enabledMarketIds.includes(a.marketId)
        )

        dispatch({
          addresses
        })
      }

      if (!address.id && addresses) {
        // Get last saved address and market
        const lastSavedAddress = addresses.filter(a => !!a.marketId)[0]
        if (!lastSavedAddress) {
          return
        }

        const lastSavedMarket = markets.find(
          m => lastSavedAddress.marketId === m.id
        )

        dispatch({
          address: lastSavedAddress,
          market: lastSavedMarket
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markets, allowAddressSelect, addresses, address, me])

  useEffect(() => {
    if (allowAddressSelect === hasSavedAddress) {
      return
    }

    dispatch({
      allowAddressSelect: hasSavedAddress
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSavedAddress])

  if (!markets) {
    return <LoadingStep />
  }

  return (
    <>
      <SignInText {...{ dispatch, isDesktop }}>
        Booked with Glamsquad before?
      </SignInText>
      <AddressSection>
        <Header2
          align='center'
          fontSize={48}
          margin={hasSavedAddress ? '24px 0 16px' : '0 0 16px'}
        >
          Where do you want to book?
        </Header2>
        <Text
          fontSize={18}
          lineHeight='26px'
          margin='0 0 34px'
        >
          {allowAddressSelect ? `Select a saved address or add a new one - you'll choose services next.` : `Enter your address below - you'll choose services next.`}
        </Text>
        {allowAddressSelect ? (
          <AddressView
            {...{
              address,
              addresses,
              dispatch,
              markets,
              mode: ADDRESS_VIEW_MODE.SELECT,
              step
            }}
          />
        ) : (
          <AddressAutocomplete
            {...{
              currentMarket: market,
              dispatch,
              ref: ref,
            }}
          />
        )}
        <ArrowButton 
          margin='70px auto 0'
          onClick={next}
          disabled={!nextButtonEnabled}
        >
          {nextButtonLabel}
        </ArrowButton>
      </AddressSection>
      
      { !hasSavedAddress && (
        <MarketsSection>
          <div className='or'>
            <div className='or-line' />
            <Header3>
              or choose a city
            </Header3>
            <div className='or-line' />
          </div>
          <div className="markets">
            {markets.map(m => (
              <MarketButton
                key={m.id}
                onClick={selectMarket(m)}
                active={market && market.id === m.id}
              >
                {m.name}
              </MarketButton>
            ))}
          </div>
        </MarketsSection>
      )}
      <PrivacyInfo>
        <Link href="/privacypolicy">
          Privacy Policy
        </Link>
        <span>|</span>
        <a href='mailto:info@glamsquad.com'>info@glamsquad.com</a>
        <span>|</span>
        <p>SMS: 844-970-2328</p>
        <span>|</span>
        <p>Phone: 844-695-4526</p>
      </PrivacyInfo>
    </>
  )
}

One.propTypes = {
  state: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  dispatchValueAtKeyPath: PropTypes.func.isRequired,
  setIsStepValid: PropTypes.func.isRequired
}

export default One
